import React from 'react';
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails,
  TextField, IconButton, LinearProgress, MenuItem, Chip, Button,
  Card, CardContent, Grid, ToggleButtonGroup, ToggleButton, InputAdornment
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { getAssetClasses, getAssetInstrumentTypesByClass } from '../../utils/utils';

const AllocationEditor = ({ allocation, onChange, onError }) => {
  const totalAllocated = Object.values(allocation?.Percentages || {})
    .reduce((sum, val) => sum + Number(val || 0), 0);

  const totalAbsolute = Object.values(allocation?.AbsoluteValues || {})
    .reduce((sum, val) => sum + Number(val || 0), 0) +
    Object.values(allocation?.AbsoluteByInstrument || {})
      .flatMap(instruments => Object.values(instruments))
      .reduce((sum, val) => sum + Number(val || 0), 0);

  const getInstrumentAllocationType = (assetClass, instrumentType) => {
    if (allocation?.AbsoluteByInstrument?.[assetClass]?.[instrumentType] !== undefined) {
      return 'absolute';
    }
    return 'percentage';
  };

  const getClassAllocationType = (assetClass) => {
    if (allocation?.AbsoluteValues?.[assetClass] !== undefined) {
      return 'absolute';
    }
    return 'percentage';
  };

  const getAllocationClassOptions = (currentClass) =>
    getAssetClasses().filter(
      (option) => option.value === currentClass ||
        (!Object.keys(allocation?.Percentages || {}).includes(option.value) &&
         !Object.keys(allocation?.AbsoluteValues || {}).includes(option.value))
    );

  const getInstrumentOptions = (assetClass, currentInstrumentType) => {
    const usedPercentage = Object.keys(allocation?.ByInstrument?.[assetClass] || {});
    const usedAbsolute = Object.keys(allocation?.AbsoluteByInstrument?.[assetClass] || {});
    const used = [...usedPercentage, ...usedAbsolute];
    return getAssetInstrumentTypesByClass(assetClass).filter(
      (option) => option.value === currentInstrumentType || !used.includes(option.value)
    );
  };

  const handleAddClass = () => {
    const existingPercentage = Object.keys(allocation?.Percentages || {});
    const existingAbsolute = Object.keys(allocation?.AbsoluteValues || {});
    const existingClasses = [...existingPercentage, ...existingAbsolute];
    const availableClass = getAssetClasses().find((option) => !existingClasses.includes(option.value));
    if (!availableClass) {
      onError('Todas as classes de ativo ja foram adicionadas.');
      return;
    }
    const instrumentOptions = getAssetInstrumentTypesByClass(availableClass.value);
    const initialInstruments = instrumentOptions.length > 0 ? { [instrumentOptions[0].value]: 0 } : {};

    onChange({
      ...allocation,
      Percentages: {
        ...allocation?.Percentages,
        [availableClass.value]: 0,
      },
      ByInstrument: {
        ...allocation?.ByInstrument,
        [availableClass.value]: initialInstruments,
      },
    });
  };

  const handleRemoveClass = (assetClass) => {
    const { [assetClass]: _, ...restPercentages } = allocation?.Percentages || {};
    const { [assetClass]: __, ...restByInstrument } = allocation?.ByInstrument || {};
    const { [assetClass]: ___, ...restAbsoluteValues } = allocation?.AbsoluteValues || {};
    const { [assetClass]: ____, ...restAbsoluteByInstrument } = allocation?.AbsoluteByInstrument || {};

    onChange({
      ...allocation,
      Percentages: restPercentages,
      ByInstrument: restByInstrument,
      AbsoluteValues: restAbsoluteValues,
      AbsoluteByInstrument: restAbsoluteByInstrument,
    });
  };

  const handleClassChange = (oldClass, newClass) => {
    const existsInPercentage = allocation?.Percentages?.[newClass] !== undefined;
    const existsInAbsolute = allocation?.AbsoluteValues?.[newClass] !== undefined;
    if (existsInPercentage || existsInAbsolute) {
      onError('Classe de ativo ja existe.');
      return;
    }

    const updatedPercentages = { ...allocation?.Percentages };
    const updatedByInstrument = { ...allocation?.ByInstrument };
    const updatedAbsoluteValues = { ...allocation?.AbsoluteValues };
    const updatedAbsoluteByInstrument = { ...allocation?.AbsoluteByInstrument };

    if (updatedPercentages[oldClass] !== undefined) {
      updatedPercentages[newClass] = updatedPercentages[oldClass];
      delete updatedPercentages[oldClass];
    }
    if (updatedAbsoluteValues[oldClass] !== undefined) {
      updatedAbsoluteValues[newClass] = updatedAbsoluteValues[oldClass];
      delete updatedAbsoluteValues[oldClass];
    }
    if (updatedByInstrument[oldClass]) {
      updatedByInstrument[newClass] = updatedByInstrument[oldClass];
      delete updatedByInstrument[oldClass];
    }
    if (updatedAbsoluteByInstrument[oldClass]) {
      updatedAbsoluteByInstrument[newClass] = updatedAbsoluteByInstrument[oldClass];
      delete updatedAbsoluteByInstrument[oldClass];
    }

    onChange({
      ...allocation,
      Percentages: updatedPercentages,
      ByInstrument: updatedByInstrument,
      AbsoluteValues: updatedAbsoluteValues,
      AbsoluteByInstrument: updatedAbsoluteByInstrument,
    });
  };

  const handleAddInstrument = (assetClass) => {
    const options = getAssetInstrumentTypesByClass(assetClass);
    const existingPercentage = allocation?.ByInstrument?.[assetClass] || {};
    const existingAbsolute = allocation?.AbsoluteByInstrument?.[assetClass] || {};
    const available = options.find((opt) =>
      existingPercentage[opt.value] === undefined && existingAbsolute[opt.value] === undefined
    );

    if (!available) {
      onError('Todos os tipos de ativo ja foram adicionados para essa classe.');
      return;
    }

    onChange({
      ...allocation,
      ByInstrument: {
        ...allocation?.ByInstrument,
        [assetClass]: {
          ...existingPercentage,
          [available.value]: 0,
        },
      },
    });
  };

  const handleRemoveInstrument = (assetClass, instrumentType) => {
    const classInstruments = allocation?.ByInstrument?.[assetClass] || {};
    const classAbsoluteInstruments = allocation?.AbsoluteByInstrument?.[assetClass] || {};
    const { [instrumentType]: _, ...restPercentage } = classInstruments;
    const { [instrumentType]: __, ...restAbsolute } = classAbsoluteInstruments;

    const classTotal = Object.values(restPercentage).reduce((sum, current) => sum + Number(current || 0), 0);

    onChange({
      ...allocation,
      ByInstrument: {
        ...allocation?.ByInstrument,
        [assetClass]: restPercentage,
      },
      AbsoluteByInstrument: {
        ...allocation?.AbsoluteByInstrument,
        [assetClass]: restAbsolute,
      },
      Percentages: {
        ...allocation?.Percentages,
        [assetClass]: classTotal,
      },
    });
  };

  const handleInstrumentChange = (assetClass, instrumentType, value) => {
    const numValue = value === '' ? 0 : Number(value);
    const allocationType = getInstrumentAllocationType(assetClass, instrumentType);

    if (allocationType === 'absolute') {
      const classAbsoluteInstruments = allocation?.AbsoluteByInstrument?.[assetClass] || {};
      onChange({
        ...allocation,
        AbsoluteByInstrument: {
          ...allocation?.AbsoluteByInstrument,
          [assetClass]: {
            ...classAbsoluteInstruments,
            [instrumentType]: numValue,
          },
        },
      });
    } else {
      const classInstruments = allocation?.ByInstrument?.[assetClass] || {};
      const updatedClassInstruments = {
        ...classInstruments,
        [instrumentType]: numValue,
      };
      const classTotal = Object.values(updatedClassInstruments).reduce(
        (sum, current) => sum + Number(current || 0),
        0
      );

      onChange({
        ...allocation,
        ByInstrument: {
          ...allocation?.ByInstrument,
          [assetClass]: updatedClassInstruments,
        },
        Percentages: {
          ...allocation?.Percentages,
          [assetClass]: classTotal,
        },
      });
    }
  };

  const handleAllocationTypeChange = (assetClass, instrumentType, newType) => {
    if (!newType) return;

    const currentType = getInstrumentAllocationType(assetClass, instrumentType);
    if (currentType === newType) return;

    const classInstruments = { ...allocation?.ByInstrument?.[assetClass] || {} };
    const classAbsoluteInstruments = { ...allocation?.AbsoluteByInstrument?.[assetClass] || {} };

    if (newType === 'absolute') {
      const value = classInstruments[instrumentType] || 0;
      delete classInstruments[instrumentType];
      classAbsoluteInstruments[instrumentType] = value * 1000;
    } else {
      delete classAbsoluteInstruments[instrumentType];
      classInstruments[instrumentType] = 0;
    }

    const classTotal = Object.values(classInstruments).reduce(
      (sum, current) => sum + Number(current || 0),
      0
    );

    onChange({
      ...allocation,
      ByInstrument: {
        ...allocation?.ByInstrument,
        [assetClass]: classInstruments,
      },
      AbsoluteByInstrument: {
        ...allocation?.AbsoluteByInstrument,
        [assetClass]: classAbsoluteInstruments,
      },
      Percentages: {
        ...allocation?.Percentages,
        [assetClass]: classTotal,
      },
    });
  };

  const handleInstrumentTypeChange = (assetClass, oldInstrumentType, newInstrumentType) => {
    const classInstruments = allocation?.ByInstrument?.[assetClass] || {};
    const classAbsoluteInstruments = allocation?.AbsoluteByInstrument?.[assetClass] || {};

    if (classInstruments[newInstrumentType] !== undefined ||
        classAbsoluteInstruments[newInstrumentType] !== undefined) {
      onError('Tipo de ativo ja existe para essa classe.');
      return;
    }

    const allocationType = getInstrumentAllocationType(assetClass, oldInstrumentType);

    if (allocationType === 'absolute') {
      const updatedAbsolute = { ...classAbsoluteInstruments };
      updatedAbsolute[newInstrumentType] = updatedAbsolute[oldInstrumentType];
      delete updatedAbsolute[oldInstrumentType];

      onChange({
        ...allocation,
        AbsoluteByInstrument: {
          ...allocation?.AbsoluteByInstrument,
          [assetClass]: updatedAbsolute,
        },
      });
    } else {
      const updatedClassInstruments = { ...classInstruments };
      updatedClassInstruments[newInstrumentType] = updatedClassInstruments[oldInstrumentType];
      delete updatedClassInstruments[oldInstrumentType];

      onChange({
        ...allocation,
        ByInstrument: {
          ...allocation?.ByInstrument,
          [assetClass]: updatedClassInstruments,
        },
      });
    }
  };

  const getProgressColor = () => {
    if (totalAllocated > 100) return 'error';
    if (totalAllocated === 100) return 'success';
    return 'primary';
  };

  const getChipColor = () => {
    if (totalAllocated > 100) return 'error';
    if (totalAllocated === 100) return 'success';
    return 'warning';
  };

  const getAllClassesWithAllocations = () => {
    const percentageClasses = Object.keys(allocation?.Percentages || {});
    const absoluteClasses = Object.keys(allocation?.AbsoluteValues || {});
    return [...new Set([...percentageClasses, ...absoluteClasses])];
  };

  const getClassValue = (assetClass) => {
    if (allocation?.AbsoluteValues?.[assetClass] !== undefined) {
      return allocation.AbsoluteValues[assetClass];
    }
    return allocation?.Percentages?.[assetClass] || 0;
  };

  const getAllInstrumentsForClass = (assetClass) => {
    const percentageInstruments = Object.keys(allocation?.ByInstrument?.[assetClass] || {});
    const absoluteInstruments = Object.keys(allocation?.AbsoluteByInstrument?.[assetClass] || {});
    return [...new Set([...percentageInstruments, ...absoluteInstruments])];
  };

  const getInstrumentValue = (assetClass, instrumentType) => {
    if (allocation?.AbsoluteByInstrument?.[assetClass]?.[instrumentType] !== undefined) {
      return allocation.AbsoluteByInstrument[assetClass][instrumentType];
    }
    return allocation?.ByInstrument?.[assetClass]?.[instrumentType] || 0;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCurrencyInput = (value) => {
    const cents = Math.round((value || 0) * 100);
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  };

  const handleCurrencyKeyDown = (e, assetClass, instrumentType, currentValue) => {
    const currentCents = Math.round((currentValue || 0) * 100);

    if (e.key === 'Backspace') {
      e.preventDefault();
      const newCents = Math.floor(currentCents / 10);
      handleInstrumentChange(assetClass, instrumentType, newCents / 100);
      return;
    }

    if (e.key === 'Delete') {
      e.preventDefault();
      handleInstrumentChange(assetClass, instrumentType, 0);
      return;
    }

    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const digit = parseInt(e.key, 10);
      const newCents = currentCents * 10 + digit;
      handleInstrumentChange(assetClass, instrumentType, newCents / 100);
      return;
    }

    const allowedKeys = ['Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Alocacao de Ativos
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={`%: ${totalAllocated}%`}
              color={getChipColor()}
              size="small"
            />
            {totalAbsolute > 0 && (
              <Chip
                label={`Fixo: ${formatCurrency(totalAbsolute)}`}
                color="info"
                size="small"
              />
            )}
          </Box>
        </Box>

        <LinearProgress
          variant="determinate"
          value={Math.min(totalAllocated, 100)}
          color={getProgressColor()}
          sx={{ mb: 3, height: 10, borderRadius: 1 }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {getAllClassesWithAllocations().map((assetClass) => {
            const classValue = getClassValue(assetClass);
            const classType = getClassAllocationType(assetClass);
            const instruments = getAllInstrumentsForClass(assetClass);

            return (
              <Accordion
                key={assetClass}
                defaultExpanded
                sx={{
                  '&:before': { display: 'none' },
                  boxShadow: 1,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ backgroundColor: 'action.hover' }}
                >
                  <Grid container spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        select
                        size="small"
                        label="Classe"
                        value={assetClass}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleClassChange(assetClass, e.target.value);
                        }}
                        fullWidth
                      >
                        {getAllocationClassOptions(assetClass).map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Chip
                        label={classType === 'absolute' ? formatCurrency(classValue) : `${classValue}%`}
                        color={classValue > 0 ? (classType === 'absolute' ? 'info' : 'primary') : 'default'}
                        size="medium"
                        sx={{ fontWeight: 'bold', minWidth: 60 }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3} sx={{ textAlign: 'right' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveClass(assetClass);
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {instruments.map((instrumentType) => {
                      const instrValue = getInstrumentValue(assetClass, instrumentType);
                      const instrType = getInstrumentAllocationType(assetClass, instrumentType);

                      return (
                        <Grid container spacing={2} alignItems="center" key={instrumentType}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              select
                              size="small"
                              label="Tipo de Instrumento"
                              value={instrumentType}
                              onChange={(e) => handleInstrumentTypeChange(assetClass, instrumentType, e.target.value)}
                              fullWidth
                            >
                              {getInstrumentOptions(assetClass, instrumentType).map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={5} sm={3}>
                            <ToggleButtonGroup
                              value={instrType}
                              exclusive
                              onChange={(_, newType) => handleAllocationTypeChange(assetClass, instrumentType, newType)}
                              size="small"
                              fullWidth
                            >
                              <ToggleButton value="percentage" sx={{ px: 1 }}>%</ToggleButton>
                              <ToggleButton value="absolute" sx={{ px: 1 }}>R$</ToggleButton>
                            </ToggleButtonGroup>
                          </Grid>
                          <Grid item xs={5} sm={3}>
                            {instrType === 'absolute' ? (
                              <TextField
                                size="small"
                                type="text"
                                label="Valor"
                                value={formatCurrencyInput(instrValue)}
                                onChange={() => {}}
                                onKeyDown={(e) => handleCurrencyKeyDown(e, assetClass, instrumentType, instrValue)}
                                fullWidth
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">R$</InputAdornment>
                                  ),
                                }}
                              />
                            ) : (
                              <TextField
                                size="small"
                                type="number"
                                label="Porcentagem"
                                value={instrValue === 0 ? '' : instrValue}
                                onChange={(e) => handleInstrumentChange(assetClass, instrumentType, e.target.value)}
                                fullWidth
                                inputProps={{ min: 0, max: 100 }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">%</InputAdornment>
                                  ),
                                }}
                              />
                            )}
                          </Grid>
                          <Grid item xs={2} sm={2} sx={{ textAlign: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveInstrument(assetClass, instrumentType)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      );
                    })}

                    {getInstrumentOptions(assetClass, null).length > 0 && (
                      <Button
                        size="small"
                        startIcon={<AddCircleIcon />}
                        onClick={() => handleAddInstrument(assetClass)}
                        variant="text"
                      >
                        Adicionar Instrumento
                      </Button>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>

        {getAllocationClassOptions(null).length > 0 && (
          <Button
            variant="outlined"
            startIcon={<AddCircleIcon />}
            onClick={handleAddClass}
            sx={{ mt: 2 }}
            fullWidth
          >
            Adicionar Classe de Ativo
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default AllocationEditor;
